import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { inboxKeys } from "./queryKeys";
import { listMessagesForEntity } from "./api";
import { mergeWithOptimistic } from "./useMessages";
import { sortMessagesAscending } from "@/components/inbox/utils";
import type { InboxConversation, InboxMessage } from "@/types/inbox";

export type EntityMailboxType = "candidate" | "job" | "company" | "match";

/**
 * Seed the per-conversation message caches from the messages embedded in the
 * entity payload, so opening a thread renders instantly with no skeleton.
 * The seed is backdated just past useMessages' 15s staleTime, so a silent
 * background refetch through the dedicated endpoint still guarantees
 * freshness. Recently-updated caches (an actively viewed thread, an
 * optimistic send reconciling) are never clobbered.
 */
function seedMessageCaches(queryClient: QueryClient, conversations: InboxConversation[]): void {
	for (const conversation of conversations) {
		if (!conversation.messages?.length) continue;
		const key = inboxKeys.messages(conversation.id);
		const state = queryClient.getQueryState<InboxMessage[]>(key);
		if (state && state.dataUpdatedAt > Date.now() - 30_000) continue;
		const merged = mergeWithOptimistic(state?.data, sortMessagesAscending(conversation.messages));
		queryClient.setQueryData<InboxMessage[]>(key, merged, { updatedAt: Date.now() - 16_000 });
	}
}

/** Conversations linked to a CRM entity, with message caches pre-seeded. */
export function useEntityConversations(entityType: EntityMailboxType, entityId: string) {
	const queryClient = useQueryClient();
	return useQuery({
		queryKey: inboxKeys.entityConversations(entityType, entityId),
		enabled: !!entityId,
		staleTime: 30 * 1000,
		refetchOnWindowFocus: true,
		queryFn: async () => {
			const conversations = await listMessagesForEntity(entityType, entityId);
			seedMessageCaches(queryClient, conversations);
			return conversations;
		},
	});
}
